
import { defineComponent, onUnmounted, reactive, ref } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import { ColDef, GridOptions } from 'ag-grid-community';
import CustomPinnedRowCellRenderer from '@/components/tables/CustomPinnedRowCellRenderer.vue';
import IconCellRenderer from '@/components/tables/IconCellRenderer.vue';
import GenericCellRenderer from '@/components/tables/GenericCellRenderer.vue';

export default defineComponent({
    emits: ['item-clicked'],
    components: { AgGridVue, CustomPinnedRowCellRenderer, IconCellRenderer, GenericCellRenderer },
    props: {
        headers: {
            type: Array as () => ColDef[],
            default: [],
        },
        items: {
            type: Array as () => any[],
            default: [],
        },
        pinnedBottomRowData: {
            type: Array,
            default: [],
        },
        options: {
            type: Object,
            default: {},
        },
        clickableRows: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const inScreenMode = ref(true);

        const gridOptions = reactive({
            suppressCellFocus: true,
            headerHeight: 75,
            localeText: { noRowsToShow: 'Ingen data at vise' },
            ...props.options,
        }) as GridOptions;

        const setPinnedRowData = (value: any) => {
            if (value) {
                // For some reason, the pinnedRow sometimes wont't render if we don't reset it first
                gridOptions.api?.setPinnedBottomRowData([]);
                setTimeout(() => {
                    gridOptions.api?.setPinnedBottomRowData(value);
                }, 200);
            }
        };

        const beforePrintEventListener = () => {
            inScreenMode.value = false;
            setPinnedRowData(props.pinnedBottomRowData);
        };
        const afterPrintEventListener = () => {
            inScreenMode.value = true;
            setPinnedRowData(props.pinnedBottomRowData);
        };

        window.addEventListener('beforeprint', beforePrintEventListener);
        window.addEventListener('afterprint', afterPrintEventListener);

        onUnmounted(() => {
            window.removeEventListener('beforeprint', beforePrintEventListener);
            window.removeEventListener('afterprint', afterPrintEventListener);
        });

        return { gridOptions, inScreenMode, setPinnedRowData };
    },
    methods: {
        onGridReady() {
            this.gridOptions.api?.sizeColumnsToFit();
            this.gridOptions.api?.setDomLayout('autoHeight');
        },
        onSelectionChanged() {
            const selectedRows = this.gridOptions.api?.getSelectedRows();
            this.gridOptions.api?.deselectAll();
            if (selectedRows && selectedRows.length) {
                this.$emit('item-clicked', selectedRows[0]);
            }
        },
        onFirstDataRendered(params: GridOptions) {
            params.api?.sizeColumnsToFit();
        },
        onGridSizeChanged(params: GridOptions) {
            params.api?.sizeColumnsToFit();
        },
    },
    watch: {
        pinnedBottomRowData: {
            handler(newValue) {
                this.setPinnedRowData(newValue);
            },
            immediate: true,
        },
    },
});
